import React, { useState, useMemo } from 'react';
import {
  FaMapMarkerAlt,
  FaUtensils,
  FaLandmark,
  FaTree,
  FaPlus,
  FaExchangeAlt,
  FaSpinner,
  FaTimes,
  FaChevronRight,
  FaInfoCircle,
  FaDirections
} from 'react-icons/fa';
import { extractLocationsWithCoordinates } from '../utils/itineraryParser';
import { addAttractionToTrip } from '../api/client';
import ReviewLinks from './ReviewLinks';
import { usePlaceImage } from '@travel-planner/shared';

const CATEGORY_KEYWORDS = {
  restaurant: FaUtensils,
  cafe: FaUtensils,
  food: FaUtensils,
  dining: FaUtensils,
  bistro: FaUtensils,
  breakfast: FaUtensils,
  lunch: FaUtensils,
  dinner: FaUtensils,
  brunch: FaUtensils,
  meal: FaUtensils,
  museum: FaLandmark,
  palace: FaLandmark,
  temple: FaLandmark,
  cathedral: FaLandmark,
  castle: FaLandmark,
  monument: FaLandmark,
  church: FaLandmark,
  mosque: FaLandmark,
  park: FaTree,
  garden: FaTree,
  forest: FaTree,
  nature: FaTree,
  beach: FaTree,
  trail: FaTree
};

function getCategoryIcon(name) {
  const lower = name.toLowerCase();
  for (const [keyword, Icon] of Object.entries(CATEGORY_KEYWORDS)) {
    if (lower.includes(keyword)) return Icon;
  }
  return FaMapMarkerAlt;
}

/**
 * Parse the Other Points markdown directly to extract full descriptions.
 * The standard itineraryParser misses "Why worth visiting" sub-bullets,
 * so we parse the raw content ourselves.
 */
function parseAttractionsFromContent(content) {
  if (!content) return [];

  const lines = content.split('\n');
  const attractions = [];
  // Match lines like:
  //   **Name** Coordinates: (lat, lng)
  //   **Name** (MP 6.8) Coordinates: (lat, lng)
  //   **Name** (4.5/5) Coordinates: (lat, lng)
  //   **Name** (lat, lng)
  // The middle group eats any parenthetical text (milepost, rating, etc.) between name and coords
  const coordPattern = /\*\*([^*]+)\*\*(?:\s*\([^)]*\))*\s*(?:Coordinates:\s*)?\((-?\d{1,3}\.\d+),\s*(-?\d{1,3}\.\d+)\)/;
  // Degree notation: **Name:** (22.4833° N, 73.5167° E)
  const degreePattern = /\*\*([^*]+)\*\*[^(]*\((\d{1,3}\.?\d*)\s*°?\s*([NSns])[\s,]+(\d{1,3}\.?\d*)\s*°?\s*([EWew])\)[,\s]*(.*)/;

  let current = null;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const match = line.match(coordPattern);

    if (match) {
      // Save previous attraction
      if (current) attractions.push(current);
      current = {
        name: match[1].trim().replace(/:\s*$/, ''),
        lat: parseFloat(match[2]),
        lng: parseFloat(match[3]),
        description: ''
      };
      continue;
    }

    // Try degree notation
    const degMatch = line.match(degreePattern);
    if (degMatch) {
      if (current) attractions.push(current);
      let lat = parseFloat(degMatch[2]);
      let lng = parseFloat(degMatch[4]);
      if (degMatch[3].toUpperCase() === 'S') lat = -lat;
      if (degMatch[5].toUpperCase() === 'W') lng = -lng;
      const rawDesc = degMatch[6] || '';
      // Extract description after distance info, before [More Info]
      const descPart = rawDesc.replace(/\[More Info\]\([^)]*\)/gi, '').replace(/^\d+\s*miles?\s+from\s+[^.]+\.\s*/i, '').trim();
      current = {
        name: degMatch[1].trim().replace(/:\s*$/, ''),
        lat,
        lng,
        description: descPart
      };
      continue;
    }

    if (!current) continue;

    const trimmed = line.trim();
    if (!trimmed) continue;

    // Strip leading bullet markers (* , - , •) to get clean text
    const clean = trimmed.replace(/^[-*•]+\s*/, '');

    // Extract "Why worth visiting" / "Why it's worth visiting" text
    const whyMatch = clean.match(/\*\*(?:Why(?:\s+it'?s?)?\s+worth\s+visiting):?\*\*\s*(.*)/i);
    if (whyMatch) {
      current.description = whyMatch[1]
        .replace(/\([\w\s,]+\)\s*\.?\s*$/, '') // Remove trailing parenthetical tags like (Nature, adventure).
        .replace(/\s+/g, ' ')
        .trim();
      continue;
    }

    // Also grab a plain description bullet that isn't a metadata field
    if (!current.description && !/^\*\*/.test(clean) && !/^\[/.test(clean) &&
        !/^distance/i.test(clean) && !/^information url/i.test(clean) &&
        !/^more info/i.test(clean) && clean.length > 20) {
      current.description = clean
        .replace(/\s+/g, ' ')
        .trim();
    }
  }
  if (current) attractions.push(current);

  return attractions.filter(a => a.lat >= -90 && a.lat <= 90 && a.lng >= -180 && a.lng <= 180);
}

// Sub-component to enable usePlaceImage hook per card
function AttractionPanelCard({ attraction, index, isOwner, destination, isLoading, isPickingReplace, onAdd, onToggleReplace, itineraryDays, onReplace }) {
  const IconComponent = getCategoryIcon(attraction.name);
  const imageUrl = usePlaceImage(attraction.name, attraction.lat, attraction.lng);

  return (
    <div>
      <div
        className="border border-neutral-200 rounded-xl p-4 hover:border-amber-300 hover:shadow-md transition-all"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt={attraction.name}
                  loading="lazy"
                  className="w-[72px] h-[72px] rounded-lg object-cover flex-shrink-0"
                />
              ) : (
                <div className="w-[72px] h-[72px] rounded-lg bg-neutral-100 flex items-center justify-center flex-shrink-0">
                  <div className="text-center">
                    <IconComponent className="text-neutral-300 text-lg mx-auto" />
                    <span className="text-[9px] text-neutral-400 font-medium">No Image</span>
                  </div>
                </div>
              )}
              <h3 className="font-semibold text-neutral-800">{attraction.name}</h3>
            </div>
            {attraction.description && (
              <p className="text-sm text-neutral-600 mb-2">{attraction.description}</p>
            )}
            <p className="text-xs text-neutral-400 mb-3">
              {attraction.lat.toFixed(4)}, {attraction.lng.toFixed(4)}
            </p>

            {/* Action links row */}
            <div className="flex items-center flex-wrap gap-2">
              <a
                href={`https://www.google.com/search?q=${encodeURIComponent(attraction.name + (destination ? ' ' + destination : ''))}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition-colors"
              >
                <FaInfoCircle size={11} />
                More Info
              </a>
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${attraction.lat},${attraction.lng}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-sky-600 bg-sky-50 hover:bg-sky-100 rounded-lg transition-colors"
              >
                <FaMapMarkerAlt size={11} />
                View on Map
              </a>
              <a
                href={`https://www.google.com/maps/dir/?api=1&destination=${attraction.lat},${attraction.lng}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-teal-600 bg-teal-50 hover:bg-teal-100 rounded-lg transition-colors"
              >
                <FaDirections size={11} />
                Directions
              </a>
              <ReviewLinks
                name={attraction.name}
                location={destination}
                coordinates={{ lat: attraction.lat, lng: attraction.lng }}
              />
            </div>
          </div>

          {isOwner && (
            <div className="flex-shrink-0 flex flex-col gap-2">
              <button
                onClick={() => onAdd(attraction, index)}
                disabled={isLoading}
                className={`flex items-center gap-1 px-3 py-2 rounded-lg font-medium text-sm transition-all ${
                  isLoading
                    ? 'bg-neutral-200 text-neutral-400 cursor-not-allowed'
                    : 'bg-amber-500 text-white hover:bg-amber-600 shadow-sm hover:shadow-md'
                }`}
              >
                {isLoading && !isPickingReplace ? (
                  <FaSpinner className="animate-spin" />
                ) : (
                  <>
                    <FaPlus className="text-xs" />
                    Add to Trip
                  </>
                )}
              </button>
              <button
                onClick={() => onToggleReplace(index)}
                disabled={isLoading}
                className={`flex items-center gap-1 px-3 py-2 rounded-lg font-medium text-sm transition-all border ${
                  isPickingReplace
                    ? 'border-amber-400 bg-amber-50 text-amber-700'
                    : 'border-neutral-300 text-neutral-600 hover:border-amber-300 hover:text-amber-600'
                } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <FaExchangeAlt className="text-xs" />
                Replace a Stop
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Inline replace picker */}
      {isPickingReplace && itineraryDays && (
        <div className="mt-3 border border-neutral-200 rounded-xl bg-white shadow-lg overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 bg-neutral-50 border-b border-neutral-100">
            <p className="text-sm text-neutral-600">
              Replace with <span className="font-semibold text-amber-600">{attraction.name}</span>
            </p>
            <button
              onClick={() => onToggleReplace(null)}
              className="text-neutral-400 hover:text-neutral-600 transition-colors p-1 rounded-md hover:bg-neutral-200"
            >
              <FaTimes size={12} />
            </button>
          </div>
          <div className="max-h-72 overflow-y-auto divide-y divide-neutral-50">
            {itineraryDays.map((day) => {
              const stops = day.locations.filter(loc => !loc.isAccommodation);
              if (stops.length === 0) return null;
              return (
                <div key={day.day} className="py-2 px-2">
                  <div className="bg-sky-50 rounded-lg px-3 py-1.5 mb-1">
                    <p className="text-xs font-semibold text-sky-700">
                      {day.dayTitle || `Day ${day.day}`}
                    </p>
                  </div>
                  {stops.map((loc, locIdx) => (
                    <button
                      key={locIdx}
                      onClick={() => onReplace(attraction, index, loc.name, day.day)}
                      className="w-full text-left flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-neutral-700 hover:bg-amber-50 hover:text-amber-700 transition-colors"
                    >
                      <FaMapMarkerAlt className="text-[10px] text-neutral-300" />
                      <span>{loc.name}</span>
                    </button>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

const PAID_TIERS = ['individual', 'group', 'starter_agent', 'professional_agent', 'enterprise_agency', 'promotional', 'unlimited', 'admin'];
const FREE_ATTRACTION_LIMIT = 2;

const AttractionsPanel = ({
  content,
  itineraryDays,
  requestId,
  isOwner,
  destination,
  onAttractionAdded,
  onAttractionError,
  userTier,
  limitsWaived = false,
  onViewPricing
}) => {
  const [addingIndex, setAddingIndex] = useState(null);
  const [replacingIndex, setReplacingIndex] = useState(null);
  const [addedCount, setAddedCount] = useState(0);
  // limitsWaived: non-paid user on a short trip (<= 3 days) gets full access.
  const isFreeUser = !limitsWaived && !PAID_TIERS.includes(userTier || 'free');
  const hasReachedLimit = isFreeUser && addedCount >= FREE_ATTRACTION_LIMIT;

  const attractions = useMemo(() => {
    if (!content) return [];
    // Use our custom parser that extracts "Why worth visiting" descriptions
    const parsed = parseAttractionsFromContent(content);
    if (parsed.length > 0) return parsed;
    // Fallback to generic parser
    return extractLocationsWithCoordinates(content);
  }, [content]);

  const handleAdd = async (attraction, index) => {
    if (hasReachedLimit) {
      if (onViewPricing) onViewPricing();
      return;
    }
    setAddingIndex(index);
    try {
      await addAttractionToTrip(requestId, {
        name: attraction.name,
        lat: attraction.lat,
        lng: attraction.lng,
        description: attraction.description || null
      });
      setAddedCount(prev => prev + 1);
      onAttractionAdded(attraction.name);
    } catch (err) {
      console.error('Failed to add attraction:', err);
      const detail = err.response?.data?.detail || '';
      if (detail.includes('Upgrade') || detail.includes('Free plan')) {
        if (onViewPricing) onViewPricing();
        return;
      }
      onAttractionError(attraction.name);
    } finally {
      setAddingIndex(null);
    }
  };

  const handleReplace = async (attraction, index, stopName, stopDay) => {
    if (hasReachedLimit) {
      if (onViewPricing) onViewPricing();
      return;
    }
    setAddingIndex(index);
    setReplacingIndex(null);
    try {
      await addAttractionToTrip(requestId, {
        name: attraction.name,
        lat: attraction.lat,
        lng: attraction.lng,
        description: attraction.description || null,
        replace_stop_name: stopName,
        replace_stop_day: stopDay
      });
      setAddedCount(prev => prev + 1);
      onAttractionAdded(attraction.name);
    } catch (err) {
      console.error('Failed to replace stop:', err);
      const detail = err.response?.data?.detail || '';
      if (detail.includes('Upgrade') || detail.includes('Free plan')) {
        if (onViewPricing) onViewPricing();
        return;
      }
      onAttractionError(attraction.name);
    } finally {
      setAddingIndex(null);
    }
  };

  // Fallback to plain text if parsing yields no results
  if (attractions.length === 0) {
    return (
      <div className="prose prose-neutral max-w-none whitespace-pre-wrap text-neutral-700">
        {content}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {isFreeUser && (
        <div className={`flex items-center justify-between p-3 rounded-lg border ${hasReachedLimit ? 'bg-amber-50 border-amber-200' : 'bg-sky-50 border-sky-200'}`}>
          <p className={`text-sm font-medium ${hasReachedLimit ? 'text-amber-700' : 'text-sky-700'}`}>
            {hasReachedLimit
              ? 'You\'ve reached the free plan limit for adding attractions.'
              : `Free plan: ${addedCount}/${FREE_ATTRACTION_LIMIT} attractions added`}
          </p>
          {hasReachedLimit && onViewPricing && (
            <button
              onClick={onViewPricing}
              className="px-3 py-1.5 text-xs font-semibold bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors"
            >
              Upgrade Plan
            </button>
          )}
        </div>
      )}
      {attractions.map((attraction, index) => (
        <AttractionPanelCard
          key={index}
          attraction={attraction}
          index={index}
          isOwner={isOwner}
          destination={destination}
          isLoading={addingIndex === index}
          isPickingReplace={replacingIndex === index}
          onAdd={handleAdd}
          onToggleReplace={(idx) => setReplacingIndex(idx === null ? null : (replacingIndex === idx ? null : idx))}
          itineraryDays={itineraryDays}
          onReplace={handleReplace}
        />
      ))}
    </div>
  );
};

export default AttractionsPanel;
